<template>
    <div class="row">
        <div class="d-flex justify-content-end align-items-center flex-wrap">
            <button v-if="hasPermission('permission_transactions_export')" type="button"
                class="btn btn-sm btn-outline-secondary print-barcode me-2" @click="downloadExcel">
                <i class="fa-solid fa-file-csv"></i>
                {{ $t('button_download_excel') }}
            </button>

            <div class="block-options content-heading">
                <select class="btn-block-option" v-model="perPage" @change="updatePerPage">
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                </select>
            </div>
        </div>
    </div>

    <!-- Transactions Table -->
    <div class="block-rounded block">
        <div class="block-header block-header-default d-flex justify-content-between align-items-center">
            <div>
                <h3 class="block-title m-0 d-flex align-items-center justify-content-between flex-wrap">
                    <div>
                        {{ $t('title_result') }} <span>{{ filteredTransactions.length }}</span>
                        <span v-if="selectedTransactions.length > 0" class="text-muted ms-2">
                            ( {{ $t('title_selected') }} {{ selectedTransactions.length }} )
                        </span>
                    </div>
                    <div v-if="selectedTransactions.length > 0" class="btn-group mt-2 mt-md-0 ms-md-3" role="group">
                        <button v-if="selectedTransactions.length !== filteredTransactions.length" type="button"
                            class="btn btn-sm btn-alt-primary" @click="toggleSelectAll">
                            <i class="fa-solid fa-list-check me-1"></i> Select All
                        </button>

                        <button v-else type="button" class="btn btn-sm btn-alt-secondary" @click="deselectAll">
                            <i class="fa-solid fa-xmark me-1"></i> Clear All
                        </button>
                    </div>

                </h3>

            </div>
        </div>

        <div class="block-content block-content-full">
            <div class="table-responsive">
                <table class="table-sm table-vcenter fs-sm table-hover table" style="table-layout: auto; width: 100%">
                    <thead>
                        <tr class="text-center fs-6">
                            <th style="width: 90px">{{ $t('table_reference') }}</th>
                            <th style="width: 100px">{{ $t('table_debtor') }}</th>
                            <th style="width: 100px">{{ $t('table_creditor') }}</th>
                            <th style="width: 80px">{{ $t('table_value') }}</th>
                            <th style="width: 100px">{{ $t('table_notes') }}</th>
                            <th style="width: 100px">{{ $t('table_issued_by') }}</th>
                            <th style="width: 120px">{{ $t('table_issued_at') }}</th>
                            <th style="width: 90px"></th>
                        </tr>
                        <tr>
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search " />
                                    <InputText v-model="filters.referenceSearch" type="text" size="small"
                                        class="w-100 h-9" />
                                </IconField>
                            </th>
                            <th>
                                <MultiSelect v-model="filters.debtorFilter" :placeholder="$t('placeholder.any')" :options="accounts"
                                    optionLabel="title" optionValue="_id" filter :maxSelectedLabels="3"
                                    class="w-full text-sm h-9" :selectionLimit="null">
                                    <template #option="slotProps">
                                        <div class="text-sm flex items-center text-sm gap-2">
                                            {{ slotProps.option.title }}
                                        </div>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <MultiSelect v-model="filters.creditorFilter" :placeholder="$t('placeholder.any')" :options="accounts"
                                    optionLabel="title" optionValue="_id" filter :maxSelectedLabels="3"
                                    class="w-full text-sm h-9" :selectionLimit="null">
                                    <template #option="slotProps">
                                        <div class="text-sm flex items-center text-sm gap-2">
                                            {{ slotProps.option.title }}
                                        </div>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search" />
                                    <InputText v-model="filters.transactionValueSearch" type="text" size="small"
                                        class="w-100 h-9" />
                                </IconField>
                            </th>
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search" />
                                    <InputText type="text" v-model="filters.noteSearch" size="small"
                                        class="w-100 h-9" />
                                </IconField>
                            </th>
                            <th>
                                <MultiSelect v-model="filters.issuedByFilter" :options="users" optionLabel="first_name"
                                    optionValue="_id" :placeholder="$t('placeholder.any')" filter :maxSelectedLabels="3"
                                    class="w-full text-sm h-9" :selectionLimit="null">
                                    <template #option="slotProps">
                                        <div class="text-sm flex items-center text-sm gap-2">
                                            <img :alt="slotProps.option.name" :src=slotProps.option.avatar
                                                class="w-8 h-8 rounded-full object-cover" style="width: 32px" />
                                            <span>{{ slotProps.option.first_name }} {{ slotProps.option.last_name
                                                }}</span>
                                        </div>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <DatePicker v-model="filters.createdAtSearch" showIcon fluid iconDisplay="input"
                                    class="h-9" size="small" />
                            </th>
                            <th class="text-center align-middle">
                                <button type="button" class="btn btn-alt-secondary btn-sm" title="Clear Filters"
                                    @click="clearFilters">
                                    <i class="fa fa-solid fa-trash"></i>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(transaction, index) in paginatedTransactions" :key="transaction._id"
                            class="text-center" :class="[
                                isSelected(transaction) ? 'table-primary selected-row' : ''
                            ]" @click="selectTransaction(transaction, index, $event)">
                            <td class="text-truncate" style="max-width: 90px">
                                <a :href="`/partner/transaction/details/view/${transaction._id}`"
                                    @click.prevent="navigateTo(transaction._id)" @click.stop>
                                    {{ transaction.reference }}
                                </a>
                            </td>
                            <td class="text-truncate" style="max-width: 100px">
                                <template v-if="transaction.debtor">
                                    <a :href="`/accounts/${transaction.debtor_account_id}`" @click.stop>
                                        {{ transaction.debtor.title }}
                                    </a>
                                </template>
                                <span v-else class="text-danger">No Client Data</span>
                            </td>
                            <td class="text-truncate" style="max-width: 100px">
                                <template v-if="transaction.creditor">
                                    <a :href="`/accounts/${transaction.creditor_account_id}`" @click.stop>
                                        {{ transaction.creditor.title }}
                                    </a>
                                </template>
                                <span v-else class="text-danger">No Client Data</span>
                            </td>
                            <td class="text-truncate" style="max-width: 100px">{{
                                formatNumber(transaction.transaction_value) }}
                                {{ defaultCurrencyAcronym }}</td>
                            <td class="text-truncate" style="max-width: 100px">{{ transaction.transaction_notes }}</td>
                            <td class="text-truncate" style="max-width: 100px">
                                <template v-if="transaction.creator?.first_name">
                                    <a :href="`/profile/${transaction.created_by}`" @click.stop>
                                        {{ transaction.creator.first_name }} {{
                                            transaction.creator.last_name }}
                                    </a>
                                </template>
                                <span v-else class="badge bg-info">Unassigned</span>
                            </td>
                            <td class="text-truncate" style="max-width: 90px">{{ formatDate(transaction.created_at) }}
                            </td>
                            <td style="width: auto">
                                <button v-if="transaction.status !== 'reverted' && transaction.operation_type !== 'reversal'" type="button"
                                    class="btn btn-sm btn-alt-danger s-sm" @click.stop="revertTransaction(transaction)">
                                    <i class="fa-solid fa-rotate-left me-2"></i>
                                    {{ $t('button_revert') }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <nav aria-label="Page navigation">
        <ul class="pagination justify-content-end push">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="prevPage">
                    <i class="fa fa-angle-left"></i>
                </a>
            </li>
            <li v-for="page in pages" :key="page" class="page-item" :class="{ active: page === currentPage }">
                <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="nextPage">
                    <i class="fa fa-angle-right"></i>
                </a>
            </li>
        </ul>
    </nav>
</template>
<script>
import MultiSelect from 'primevue/multiselect';
import DatePicker from 'primevue/datepicker';

function debounce(fn, delay) {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), delay);
    };
}

export default {
    props: {
        transactions: {
            type: Array,
            default: () => []
        },
        initialSelectedTransactions: {
            type: Array,
            default: () => []
        },
    },

    data() {
        return {
            loading: true,
            perPage: 50,
            currentPage: 1,
            selectAll: false,
            selectedTransactions: [], // Local state for selected transactions
            lastSelectedIndex: null, // for shift-click range
            filters: {
                referenceSearch: null,
                debtorFilter: [],
                creditorFilter: [],
                transactionValueSearch: null,
                noteSearch: null,
                issuedByFilter: [],
                createdAtSearch: null
            },
            accounts: [],
            users: [],
            groupedUsers: [],
            isExporting: false,
            printing: false
        }
    },



    mounted() {
        this.fetchAccounts();
        this.fetchUsers();
        this.fetchUsersGroupedByRole();
    },

    watch: {
        filters: {
            handler: debounce(function () {
                this.$emit('filters-updated', {
                    ...(this.filters || {}),
                    page: this.currentPage,
                    perPage: this.perPage
                });
            }, 500), // 500ms delay
            deep: true,
            immediate: false
        },
        initialSelectedTransactions: {
            handler(newVal) {
                // Sync parent's selection with local state
                this.selectedTransactions = [...newVal];
            },
            immediate: true
        },
    },

    computed: {
        filteredTransactions() {
            const {
                referenceSearch,
                debtorFilter,
                creditorFilter,
                transactionValueSearch,
                noteSearch,
                issuedByFilter,
            } = this.filters;

            return this.transactions.filter(transaction => {
                const matchesReference = !referenceSearch ||
                    (transaction.reference && transaction.reference.toLowerCase().includes(referenceSearch.toLowerCase()));

                const matchesDebtor = debtorFilter.length === 0 ||
                    (transaction.debtor && transaction.debtor._id && debtorFilter.includes(transaction.debtor._id));

                const matchesCreditor = creditorFilter.length === 0 ||
                    (transaction.creditor && transaction.creditor._id && creditorFilter.includes(transaction.creditor._id));

                const matchesTransactionValue = !transactionValueSearch ||
                    (transaction.transaction_value !== undefined &&
                        transaction.transaction_value !== null &&
                        String(transaction.transaction_value).includes(transactionValueSearch));

                const matchesNote = !noteSearch ||
                    (transaction.transaction_notes && transaction.transaction_notes.toLowerCase().includes(noteSearch.toLowerCase()));

                const matchesIssuedBy = issuedByFilter.length === 0 ||
                    (transaction.assigned_to && issuedByFilter.includes(transaction.assigned_to));




                return matchesReference &&
                    matchesDebtor &&
                    matchesCreditor &&
                    matchesTransactionValue &&
                    matchesNote &&
                    matchesIssuedBy;
            });
        },

        selectedTransactionIds() {
            return this.selectedTransactions;
        },

        paginatedTransactions() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.filteredTransactions.slice(start, end);
        },

        totalPages() {
            return Math.ceil(this.filteredTransactions.length / this.perPage);
        },

        pages() {
            const range = [];
            const start = Math.max(1, this.currentPage - 2);
            const end = Math.min(this.totalPages, this.currentPage + 2);

            for (let i = start; i <= end; i++) {
                range.push(i);
            }

            return range;
        }
    },

    methods: {
        clearFilters() {
            this.filters = {
                referenceSearch: null,
                debtorFilter: [],
                creditorFilter: [],
                transactionValueSearch: null,
                noteSearch: null,
                issuedByFilter: [],
                createdAtSearch: null
            };
            this.currentPage = 1;
        },

        selectTransaction(transaction, index, event) {
            let updatedSelection = [...this.selectedTransactions];
            const transactionId = transaction._id;

            if (event.shiftKey && this.lastSelectedIndex !== null) {
                // Shift-click selection logic
                const start = Math.min(index, this.lastSelectedIndex);
                const end = Math.max(index, this.lastSelectedIndex);

                for (let i = start; i <= end; i++) {
                    const s = this.paginatedTransactions[i];
                    const sId = s._id;
                    if (!updatedSelection.includes(sId)) {
                        updatedSelection.push(sId);
                    }
                }
            } else {
                // Single click selection logic
                const existingIndex = updatedSelection.indexOf(transactionId);
                if (existingIndex !== -1) {
                    updatedSelection.splice(existingIndex, 1);
                } else {
                    updatedSelection.push(transactionId);
                }
                this.lastSelectedIndex = index;
            }

            // Update local state which will propagate to map
            this.selectedTransactions = updatedSelection;
        },

        isSelected(transaction) {
            return this.selectedTransactions.includes(transaction._id);
        },

        toggleSelectAll() {
            let newSelection;
            if (this.selectedTransactions.length === this.filteredTransactions.length) {
                newSelection = [];
            } else {
                newSelection = this.filteredTransactions.map(s => s._id);
            }
            this.updateSelectedTransactions(newSelection);
        },

        deselectAll() {
            this.updateSelectedTransactions([]);
        },

        updateSelectedTransactions(updatedSelection) {
            this.selectedTransactions = updatedSelection;
        },

        async hasPermission(permission) {
            try {
                const response = await axios.get(`/partner-users/check-permission/${permission}`);
                return response.data.hasPermission;
            } catch (error) {
                console.error('Error checking permission:', error);
                return false;
            }
        },

        fetchUsersGroupedByRole() {
            axios.get('/partner-users/get-auth-partner-users-grouped')
                .then(response => {
                    this.groupedUsers = response.data;
                })
                .catch(error => {
                    console.error('Error fetching users:', error);
                });
        },

        revertTransaction(transaction) {
            Swal.fire({
                title: this.$t('message_revert_transaction'),
                text: this.$t('message_revert_transaction_confirmation'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.$t('button_yes'),
                cancelButtonText: this.$t('button_no')
            }).then((result) => {
                if (result.isConfirmed) {
                    axios.post(`/transactions/revert/${transaction._id}`)
                        .then(response => {
                            Swal.fire({
                                title: this.$t('message_success'),
                                text: this.$t('message_transaction_reverted'),
                                icon: 'success',
                                showConfirmButton: false,
                                timer: 1500,
                            });
                            this.$emit('transaction-reverted', transaction._id);
                        })
                        .catch(error => {
                            console.error('Error reverting transaction:', error);
                            Swal.fire({
                                title: this.$t('message_error'),
                                text: this.$t('message_transaction_revert_failed'),
                                icon: 'error'
                            });
                        });
                }
            });
        },

        updatePerPage() {
            this.currentPage = 1;
        },

        searchTransactions() {
            this.currentPage = 1;
        },

        prevPage() {
            if (this.currentPage > 1) this.currentPage--;
        },

        nextPage() {
            if (this.currentPage < this.totalPages) this.currentPage++;
        },

        goToPage(page) {
            this.currentPage = page;
        },

        toggleStatusFilter(statusId) {
            const index = this.filters.status.indexOf(statusId);
            if (index === -1) {
                this.filters.status.push(statusId);
            } else {
                this.filters.status.splice(index, 1);
            }
            this.currentPage = 1;
        },

        navigateTo(url) {
            window.location.href = url;
        },

        formatNumber(number) {
            return new Intl.NumberFormat().format(number);
        },

        formatDate(date) {
            return new Date(date).toLocaleString();
        },

        fetchAccounts() {
            axios.get('/accounts/get-active-accounts')
                .then(response => {
                    this.accounts = response.data;
                })
                .catch(error => {
                    console.error('Error fetching users:', error);
                });
        },

        fetchUsers() {
            $.ajax({
                url: '/get-all-clients',  // Replace with your endpoint
                method: 'GET',
                success: (data) => {
                    this.users = data;
                },
                error: (xhr, status, error) => {
                    console.error('Error fetching users:', error);
                }
            });
        },

        async downloadExcel() {
            // Disable the button to prevent multiple clicks
            this.isExporting = true;

            // Perform action only if at least one checkbox is checked
            if (this.selectedTransactions.length > 0) {
                try {
                    const response = await axios.post('/transactions/export', {
                        transactionsIds: this.selectedTransactions,
                    });

                    // Handle success response (Assume the response is the Excel file as Blob)
                    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

                    // Create a link element to trigger the download
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'transactions.xlsx'); // Set the download file name

                    // Append the link to the body and trigger the download
                    document.body.appendChild(link);
                    link.click();

                    // Clean up: remove the link and revoke the URL object
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                } catch (error) {
                    // Handle error
                    console.error('Error exporting transactions:', error);

                    // Show a warning if the export fails
                    Swal.fire({
                        title: "Error!",
                        text: this.$t('message_export_failed'), // Assuming you're using Vue-i18n
                        icon: "error",
                    });
                } finally {
                    // Re-enable the button and reset exporting state
                    this.isExporting = false;
                }
            } else {
                // If no checkbox is checked, show a warning message
                Swal.fire({
                    title: "Warning!",
                    text: this.$t('message_no_transactions_selected'), // Assuming you're using Vue-i18n
                    icon: "warning",
                });

                // Re-enable the button
                this.isExporting = false;
            }
        },
    }
}
</script>


<style scoped>
.skeleton-loader {
    height: 20px;
    background-color: #f0f0f0;
    border-radius: 4px;
    margin: 5px 0;
    animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
    0% {
        opacity: 0.6;
    }

    50% {
        opacity: 0.3;
    }

    100% {
        opacity: 0.6;
    }
}

table {
    table-layout: auto;
    width: 100%;
}

td {
    white-space: normal !important;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
    /* Adjust as needed */
    word-break: break-word;
}

tr {
    cursor: pointer;
}

.status-badge:hover {
    opacity: 0.8;
}

.client_sim_number span {
    cursor: pointer;
}

.client_sim_number span:hover {
    text-decoration: underline;
}

.dropdown-menu {
    max-height: 250px;
    /* Set your desired maximum height */
    overflow-y: auto;
    /* Enable vertical scrolling when content overflows */
}

.table-active {
    background-color: #0056b3 !important;
    /* Optional: change the background for active state */
}

.selected-row {
    background-color: #007bff !important;
    /* Change to the color you want */
    color: white;
    /* Optional: change text color for contrast */
}
</style>